/**
 * WebSocket service for real-time communication with RILE Connect agent
 * Manages WebSocket connection, authentication, and message handling
 */

import { getAuth } from 'firebase/auth';

const AGENT_WS_URL =
    process.env.REACT_APP_AGENT_WS_URL || 'ws://localhost:3500';

// Error types for different failure scenarios
export const WS_ERROR_TYPES = {
    PERMISSION_DENIED: 'permission_denied',
    CONNECTION_ERROR: 'connection_error',
    AUTH_ERROR: 'auth_error',
};

class WebSocketService {
    constructor() {
        this.ws = null;
        this.connected = false;
        this.connecting = false;
        this.messageHandlers = new Map();
        this.currentThreadId = null;
        this.currentPersona = 'maya'; // Default persona
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 1000;
        this.messageQueue = [];
        this.pendingMessages = new Map(); // Track messages awaiting response
        this.lastErrorType = null; // Track the type of last error

        // Clean up WebSocket on page unload to prevent stale connections
        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', () => {
                if (this.ws) {
                    this.ws.close(1000, 'Page unload');
                    this.ws = null;
                    this.connected = false;
                    this.connecting = false;
                }
            });
        }
    }

    /**
     * Connect to WebSocket server with authentication
     * @param {string} threadId - Optional thread ID to connect to specific conversation
     * @param {string} persona - Persona to connect to ('maya' or 'elena', default: 'maya')
     * @returns {Promise<void>}
     */
    async connect(threadId = null, persona = 'maya') {
        // If already connected or connecting, clean up first to avoid stale state
        if (this.ws) {
            try {
                this.ws.close(1000, 'Reconnecting');
            } catch {
                // Ignore close errors
            }
            this.ws = null;
        }

        // Reset state flags
        this.connected = false;
        this.connecting = true;
        this.lastErrorType = null;
        this.currentPersona = persona;

        try {
            // Get Firebase auth token
            const auth = getAuth();
            const user = auth.currentUser;

            if (!user) {
                throw new Error('User not authenticated');
            }

            // Force refresh the token to ensure it's valid after page refresh
            const token = await user.getIdToken(true);

            // Build WebSocket URL with persona path
            // Format: ws://localhost:3500/maya or ws://localhost:3500/maya/{threadId}
            let wsUrl = `${AGENT_WS_URL}/${persona}`;
            if (threadId) {
                wsUrl += `/${threadId}`;
                this.currentThreadId = threadId;
            }

            // Create WebSocket connection with auth token as subprotocol
            this.ws = new WebSocket(wsUrl, [token]);

            // Set up event handlers
            this.ws.onopen = this._handleOpen.bind(this);
            this.ws.onmessage = this._handleMessage.bind(this);
            this.ws.onerror = this._handleError.bind(this);
            this.ws.onclose = this._handleClose.bind(this);

            // Wait for connection to establish
            await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    reject(new Error('WebSocket connection timeout'));
                }, 10000);

                this.ws.addEventListener(
                    'open',
                    () => {
                        clearTimeout(timeout);
                        resolve();
                    },
                    { once: true }
                );

                this.ws.addEventListener(
                    'error',
                    (error) => {
                        clearTimeout(timeout);
                        reject(error);
                    },
                    { once: true }
                );
            });
        } catch (error) {
            this.connecting = false;

            // Determine error type based on the failure
            // 403 Forbidden typically causes immediate WebSocket failure
            if (error.message?.includes('not authenticated')) {
                this.lastErrorType = WS_ERROR_TYPES.AUTH_ERROR;
            } else {
                // Check if this might be a permission denied (403)
                // WebSocket 403 failures happen immediately during handshake
                this.lastErrorType = WS_ERROR_TYPES.CONNECTION_ERROR;
            }

            console.error('Failed to connect to WebSocket:', error);
            throw error;
        }
    }

    /**
     * Get the last error type that occurred
     * @returns {string|null} Error type or null
     */
    getLastErrorType() {
        return this.lastErrorType;
    }

    /**
     * Clear the last error type
     */
    clearLastError() {
        this.lastErrorType = null;
    }

    /**
     * Disconnect from WebSocket server
     */
    disconnect() {
        if (this.ws) {
            this.ws.close(1000, 'Client disconnect');
            this.ws = null;
            this.connected = false;
            this.currentThreadId = null;
        }
    }

    /**
     * Send a message to the agent
     * @param {string} message - User message text
     * @param {Object} [metadata] - Optional metadata to include (e.g., page_context)
     * @returns {Promise<Object>} Agent response
     */
    async sendMessage(message, metadata) {
        if (!this.connected) {
            throw new Error('WebSocket not connected');
        }

        const messageId = `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        const payload = {
            type: 'message',
            action: 'send',
            data: { message, ...metadata },
            message_id: messageId,
        };

        // Send message
        this.ws.send(JSON.stringify(payload));

        // Wait for response
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                this.pendingMessages.delete(messageId);
                reject(new Error('Message timeout'));
            }, 120000); // 120 second timeout

            this.pendingMessages.set(messageId, {
                resolve: (response) => {
                    clearTimeout(timeout);
                    this.pendingMessages.delete(messageId);
                    resolve(response);
                },
                reject: (error) => {
                    clearTimeout(timeout);
                    this.pendingMessages.delete(messageId);
                    reject(error);
                },
            });
        });
    }

    /**
     * Create a new conversation
     * @returns {Promise<Object>} New conversation data with thread_id
     */
    async createNewConversation() {
        if (!this.connected) {
            throw new Error('WebSocket not connected');
        }

        const messageId = `cmd-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        const payload = {
            type: 'command',
            action: 'new_conversation',
            data: {},
            message_id: messageId,
        };

        this.ws.send(JSON.stringify(payload));

        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                this.pendingMessages.delete(messageId);
                reject(new Error('Command timeout'));
            }, 10000);

            this.pendingMessages.set(messageId, {
                resolve: (response) => {
                    clearTimeout(timeout);
                    this.pendingMessages.delete(messageId);
                    if (response.status === 'success') {
                        this.currentThreadId = response.data.thread_id;
                        resolve(response.data);
                    } else {
                        reject(
                            new Error(
                                response.data?.error ||
                                    'Failed to create conversation'
                            )
                        );
                    }
                },
                reject: (error) => {
                    clearTimeout(timeout);
                    this.pendingMessages.delete(messageId);
                    reject(error);
                },
            });
        });
    }

    /**
     * Switch to a different conversation
     * @param {string} threadId - Thread ID to switch to
     * @returns {Promise<void>}
     */
    async switchConversation(threadId) {
        // For now, we'll disconnect and reconnect with new thread ID
        // This is simpler than using the switch_conversation command
        this.disconnect();
        await this.connect(threadId);
    }

    /**
     * Get current conversation info
     * @returns {Promise<Object>} Current conversation info
     */
    async getCurrentConversation() {
        if (!this.connected) {
            throw new Error('WebSocket not connected');
        }

        const messageId = `cmd-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        const payload = {
            type: 'command',
            action: 'get_current_conversation',
            data: {},
            message_id: messageId,
        };

        this.ws.send(JSON.stringify(payload));

        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                this.pendingMessages.delete(messageId);
                reject(new Error('Command timeout'));
            }, 10000);

            this.pendingMessages.set(messageId, {
                resolve: (response) => {
                    clearTimeout(timeout);
                    this.pendingMessages.delete(messageId);
                    if (response.status === 'success') {
                        resolve(response.data);
                    } else {
                        reject(
                            new Error(
                                response.data?.error ||
                                    'Failed to get conversation'
                            )
                        );
                    }
                },
                reject: (error) => {
                    clearTimeout(timeout);
                    this.pendingMessages.delete(messageId);
                    reject(error);
                },
            });
        });
    }

    /**
     * Register a handler for incoming messages
     * @param {string} type - Message type to handle
     * @param {Function} handler - Handler function
     */
    on(type, handler) {
        if (!this.messageHandlers.has(type)) {
            this.messageHandlers.set(type, []);
        }
        this.messageHandlers.get(type).push(handler);
    }

    /**
     * Unregister a message handler
     * @param {string} type - Message type
     * @param {Function} handler - Handler function to remove
     */
    off(type, handler) {
        if (this.messageHandlers.has(type)) {
            const handlers = this.messageHandlers.get(type);
            const index = handlers.indexOf(handler);
            if (index > -1) {
                handlers.splice(index, 1);
            }
        }
    }

    /**
     * Handle WebSocket open event
     */
    _handleOpen() {
        this.connected = true;
        this.connecting = false;
        this.reconnectAttempts = 0;

        // Flush message queue
        while (this.messageQueue.length > 0) {
            const message = this.messageQueue.shift();
            this.ws.send(message);
        }

        // Notify handlers
        this._notifyHandlers('open', {});
    }

    /**
     * Handle incoming WebSocket message
     * @param {MessageEvent} event - WebSocket message event
     */
    _handleMessage(event) {
        try {
            const data = JSON.parse(event.data);

            // Capture thread_id from server responses so the client
            // knows the actual conversation thread after initial connect
            if (data.metadata?.thread_id && !this.currentThreadId) {
                this.currentThreadId = data.metadata.thread_id;
            }

            // Handle responses to pending messages
            if (
                data.in_reply_to &&
                this.pendingMessages.has(data.in_reply_to)
            ) {
                const pending = this.pendingMessages.get(data.in_reply_to);
                if (data.type === 'error') {
                    pending.reject(
                        new Error(data.data?.error || 'Unknown error')
                    );
                } else {
                    pending.resolve(data);
                }
                return;
            }

            // Notify type-specific handlers
            this._notifyHandlers(data.type, data);

            // Always notify 'message' handlers for all messages
            if (data.type !== 'message') {
                this._notifyHandlers('message', data);
            }
        } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
        }
    }

    /**
     * Handle WebSocket error
     * @param {Event} error - Error event
     */
    _handleError(error) {
        console.error('WebSocket error:', error);
        this._notifyHandlers('error', error);
    }

    /**
     * Handle WebSocket close event
     * @param {CloseEvent} event - Close event
     */
    _handleClose(event) {
        this.connected = false;
        this.connecting = false;

        // Detect permission denied (403) scenarios
        // Close code 4403 = explicit 403 Forbidden
        // Close code 1008 = Policy Violation (often used for auth failures)
        // Close code 1006 with "403" in reason = HTTP 403 during handshake
        const isPermissionDenied =
            event.code === 4403 ||
            event.code === 1008 ||
            (event.code === 1006 &&
                event.reason?.toLowerCase().includes('403')) ||
            event.reason?.toLowerCase().includes('forbidden') ||
            event.reason?.toLowerCase().includes('permission') ||
            event.reason?.toLowerCase().includes('unauthorized role');

        if (isPermissionDenied) {
            this.lastErrorType = WS_ERROR_TYPES.PERMISSION_DENIED;
        }

        this._notifyHandlers('close', {
            code: event.code,
            reason: event.reason,
            errorType: this.lastErrorType,
        });

        // Don't attempt reconnection for permission denied or normal close
        if (isPermissionDenied || event.code === 1000) {
            return;
        }

        // Attempt reconnection for other errors
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            const delay =
                this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

            setTimeout(() => {
                this.connect(this.currentThreadId).catch((err) => {
                    console.error('Reconnection failed:', err);
                });
            }, delay);
        }
    }

    /**
     * Notify registered handlers
     * @param {string} type - Event type
     * @param {*} data - Event data
     */
    _notifyHandlers(type, data) {
        if (this.messageHandlers.has(type)) {
            this.messageHandlers.get(type).forEach((handler) => {
                try {
                    handler(data);
                } catch (error) {
                    console.error(`Error in ${type} handler:`, error);
                }
            });
        }
    }

    /**
     * Get connection status
     * @returns {boolean} True if connected
     */
    isConnected() {
        return this.connected;
    }

    /**
     * Get current thread ID
     * @returns {string|null} Current thread ID
     */
    getThreadId() {
        return this.currentThreadId;
    }

    /**
     * Get current persona
     * @returns {string} Current persona ('maya' or 'elena')
     */
    getPersona() {
        return this.currentPersona;
    }
}

// Export singleton instance
export default new WebSocketService();
